import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTime } from "luxon";
import store from "redux/store";

// hooks and utilities
import { useLanguage } from "lang/Translate";
import useLoadGameData from "./useLoadGameData";
import { useLogOut } from "components/auth/LogOut";
import useEventListener from "hooks/useEventListener";
import { useScheduleDemandRefresh } from "hooks/useDemand";

//components
import Loader from "components/common/Loader/Loader";
import Navbar from "./Navbar/Navbar";
import SubsidiariesChanger from "./SubsidiariesChanger/SubsidiariesChanger";
import Box from "./Box/Box";
import FuelPriceController from "./FuelPriceController/FuelPriceController";
import StockPriceController from "./StockPriceController/StockPriceController";
import Map from "./Map/Map";
import Window from "./Window/Window";
import AdminTools from "./AdminTools/AdminTools";
import RevenuePopup from "./revenuePopup/revenuePopup";
import Overlay from "components/overlay/Overlay";
import SideWindow from "./SideWindow/SideWindow";
import Changelog from "components/Changelog/Changelog";

// common components
import SlidingTextButton from "components/common/SlidingTextButton/SlidingTextButton";
import ErrorBoundary from "components/common/ErrorBoundary/ErrorBoundary";

// classes
import Aircraft from "classes/aircraft";

//css
import "./Game.scoped.scss";

//assets
import aircraftList from "assets/json/aircraftList.json";
import airportList from "assets/json/airportList.json";

const Game = () => {
	const loadingTime = process.env.__MODE__ === "development" ? 1000 : 4000;
	const isChristmas = DateTime.utc().month === 12 || DateTime.utc().month === 1;
	const lang = useLanguage();

	const [loadingTimePassed, setLoadingTimePassed] = useState(false);
	const [routesCreated, setRoutesCreated] = useState(false);
	const { dataLoaded, error, retry } = useLoadGameData({
		onRetry: () => {
			setLoadingTimePassed(false);
			setTimeout(() => setLoadingTimePassed(true), loadingTime);
		}
	});

	const dispatch = useDispatch();

	useScheduleDemandRefresh();

	useEffect(() => {
		dispatch({ type: "SET_AIRPORTS", state: airportList });
		dispatch({ type: "SET_AIRCRAFT_LIST", state: aircraftList });
	}, [dispatch]);

	useEventListener(window, "beforeunload", e => {
		if (!store.getState().isLoggedIn) return;
		e.preventDefault();
		e.returnValue = "";
	});

	//loading
	useEffect(() => {
		const t = setTimeout(() => setLoadingTimePassed(true), loadingTime);
		return () => clearTimeout(t);
	}, [loadingTime]);

	useEffect(() => {
		if (!dataLoaded || routesCreated) return;
		(async () => {
			await Aircraft.createRoutes();
			setRoutesCreated(true);
		})();
	}, [dataLoaded, routesCreated]);

	if (!loadingTimePassed || !dataLoaded) {
		return (
			<div className={`loader ${isChristmas ? "loader--christmas" : ""}`}>
				{!error && <Loader />}
				{error && <LoadingError error={error} retry={retry} />}
			</div>
		);
	}

	return (
		<>
			<ErrorBoundary lang={lang} isChristmas={isChristmas}>
				{!routesCreated && <Loader />}

				<Navbar />
				<SubsidiariesChanger />
				<BoxController />

				<div className="fly_outs">
					<FuelPriceController />
					<StockPriceController />
				</div>

				<RevenuePopup />
				<Window />
				<Map />
				<SideWindow />
				<Changelog />
			</ErrorBoundary>

			<Overlay />
		</>
	);
};

const LoadingError = ({ error, retry }) => {
	const lang = useLanguage();
	const { signOut: logOut } = useLogOut();

	if (!error.response) {
		console.error(error);
		error.response = {
			status: "000",
			statusText: "Nieznany błąd"
		};
	}

	return (
		<div className="message" style={{ zIndex: 200, fontSize: "1.5rem" }}>
			{lang === "PL" ? "Wystąpił błąd:" : "An error has occurred:"}
			<br />
			<p className="error">
				{error.response?.status}: {error.response?.statusText}
			</p>
			<p className="space-2">{lang === "PL" ? "Proszę spróbować ponownie później." : "Please try again later."}</p>

			<SlidingTextButton
				text={lang === "PL" ? "Ponów próbę" : "Retry"}
				icon={<i className="fas fa-arrow-rotate-right" />}
				background="darkBlue"
				onClick={retry}
			/>

			<p className="space-1" />

			<SlidingTextButton
				text={lang === "PL" ? "Wyloguj" : "Log out"}
				icon={<i className="fas fa-sign-out-alt icon" />}
				background="darkBlue"
				onClick={logOut}
			/>
		</div>
	);
};

const BoxController = () => {
	const [header, setHeader] = useState("");
	const [content, setContent] = useState("");
	const tab = useSelector(store => store.box.tab);

	useEffect(() => {
		if (tab == null) return;
		const header = tab === 0 ? "powiadomienia" : tab === 1 ? "Narzędzia Administratorskie" : "";
		setHeader(header);
	}, [tab]);

	useEffect(() => {
		const content = tab === 0 ? <div>Powiadomienia</div> : tab === 1 ? <AdminTools /> : "";

		if (tab == null) {
			setTimeout(() => setContent(content), 1000);
			return;
		}

		setContent(content);
	}, [tab]);

	return (
		<Box header={header} tab={tab}>
			{content}
		</Box>
	);
};

export default Game;
